<template>
  <canvas ref="canvas"></canvas>
</template>

<script>
import Chart from 'chart.js/auto'

export default {
  name: 'DurationVsWeightChart',
  props: {
    tasks: {
      type: Array,
      required: true
    },
    selectedTask: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      chart: null
    }
  },
  mounted() {
    this.createChart()
  },
  watch: {
    tasks: {
      handler() {
        this.updateChart()
      },
      deep: true
    },
    selectedTask: {
      handler() {
        this.updateChart()
      },
      deep: true
    }
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.destroy()
    }
  },
  methods: {
    createChart() {
      const ctx = this.$refs.canvas.getContext('2d')

      this.chart = new Chart(ctx, {
        type: 'scatter',
        data: {
          datasets: this.getChartDatasets()
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,

          plugins: {
            title: {
              display: true,
              text: 'Duration vs Total Weight'
            },
            legend: {
              labels: {
                filter: function (item,) {
                  // Logic to remove a particular legend item goes here
                  return item.text !== 'Tasks';
                }
              }
            },
            tooltip: {
              callbacks: {
                label: (context) => {
                  const task = this.tasks[context.dataIndex]
                  return `${task.title}: ${this.formatWeight(context.parsed.x * 1000)}, ${context.parsed.y}h`
                }
              }
            }
          },
          scales: {
            x: {
              title: {
                display: true,
                text: 'Total Weight (kg)'
              },
              beginAtZero: true
            },
            y: {
              title: {
                display: true,
                text: 'Duration (hours)'
              },
              beginAtZero: true
            }
          }
        }
      })
    },

    updateChart() {
      if (this.chart) {
        this.chart.data.datasets = this.getChartDatasets()
        this.chart.update()
      }
    },

    getChartDatasets() {
      const datasets = [{
        label: 'Tasks',
        data: this.getScatterData(),
        backgroundColor: 'rgba(25, 118, 210, 0.6)',
        borderColor: 'rgba(25, 118, 210, 1)',
        pointRadius: 6,
        pointHoverRadius: 8,
        showLine: false,
      }, {
        label: 'Trend',
        data: this.getTrendLineData(),
        type: 'line',
        backgroundColor: 'transparent',
        borderColor: 'rgba(244, 67, 54, 0.8)',
        borderWidth: 2,
        pointRadius: 0,
        fill: false,
        tension: 0
      }];

      // Add API Rate line if selectedTask has an API rate
      if (this.selectedTask && this.selectedTask.apiRate && parseFloat(this.selectedTask.apiRate) > 0) {
        datasets.push({
          label: `${this.selectedTask.apiRate} kg/hr`,
          data: this.getApiRateLineData(),
          type: 'line',
          backgroundColor: 'transparent',
          borderColor: 'rgba(76, 175, 80, 0.8)',
          borderWidth: 2,
          pointRadius: 0,
          fill: false,
          tension: 0,
          borderDash: [5, 5] // Dashed line to distinguish from trend line
        });
      }

      return datasets;
    },

    getScatterData() {
      return this.tasks.map(task => ({
        x: this.getTotalWeightKg(task.description),
        y: this.getDurationHours(task.elapsed)
      }))
    },

    getTrendLineData() {
      const data = this.getScatterData()
      if (data.length < 2) return []

      // Calculate linear regression (y = mx + b)
      const n = data.length
      const sumX = data.reduce((sum, point) => sum + point.x, 0)
      const sumY = data.reduce((sum, point) => sum + point.y, 0)
      const sumXY = data.reduce((sum, point) => sum + (point.x * point.y), 0)
      const sumXX = data.reduce((sum, point) => sum + (point.x * point.x), 0)

      const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX)
      const intercept = (sumY - slope * sumX) / n

      // Get min and max x values for the trend line
      const minX = Math.min(...data.map(point => point.x))
      const maxX = Math.max(...data.map(point => point.x))

      // Return two points to draw the line
      return [
        {x: minX, y: slope * minX + intercept},
        {x: maxX, y: slope * maxX + intercept}
      ]
    },

    getApiRateLineData() {
      if (!this.selectedTask || !this.selectedTask.apiRate) return []

      const apiRate = parseFloat(this.selectedTask.apiRate)
      if (apiRate <= 0) return []

      // Get the weight range from the scatter data
      const data = this.getScatterData()
      if (data.length === 0) return []

      const minX = Math.min(...data.map(point => point.x))
      const maxX = Math.max(...data.map(point => point.x))

      // Calculate duration based on API rate: duration = weight / rate
      // API rate is in kg/hr, so duration in hours = weight in kg / rate in kg/hr
      return [
        {x: minX, y: minX / apiRate},
        {x: maxX, y: maxX / apiRate}
      ]
    },

    getDurationHours(elapsed) {
      if (!elapsed) return 0
      return Number((elapsed / (1000 * 60 * 60)).toFixed(2))
    },

    getTotalWeightKg(description) {
      const grams = this.calculateTotalWeight(description)
      return grams / 1000
    },

    calculateTotalWeight(description) {
      if (!description) return 0

      let totalWeight = 0
      const lines = description.split('\n')

      for (const line of lines) {
        const trimmedLine = line.trim()
        if (trimmedLine) {
          const weightMatch = trimmedLine.match(/^(\d+(?:\.\d+)?)\s*(g|kg|grams?|kilograms?)/i)
          if (weightMatch) {
            const value = parseFloat(weightMatch[1])
            const unit = weightMatch[2].toLowerCase()

            if (unit.startsWith('kg') || unit.startsWith('kilogram')) {
              totalWeight += value * 1000
            } else {
              totalWeight += value
            }
          }
        }
      }

      return totalWeight
    },

    formatWeight(grams) {
      if (grams === 0) return '0g'
      if (grams >= 1000) {
        return `${(grams / 1000).toFixed(1)}kg`
      } else {
        return `${grams.toFixed(0)}g`
      }
    }
  }
}
</script>

<style scoped>
</style>